import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Car } from "./CarService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const VEHICLE_RECALL_FIELDS = `
  id
  carId
  vehicleRecallId
  dismissedAt
  resolvedAt
  userNotes
  status
  createdAt
  source
  campaignNumber
  manufacturer
  component
  systemType
  summary
  consequence
  remedy
  recallNotes
  reportReceivedDate
  parkIt
  parkOutside
  otaUpdate
  car {
    id
    label
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const VEHICLE_RECALL_LIST_QUERY = gql`
  query VehicleRecallList($filter: VehicleRecallFilter, $params: PaginationAndSorting) {
    vehicleRecallList(filter: $filter, params: $params) {
      code
      data {
        ${VEHICLE_RECALL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_RECALL_COUNTS_QUERY = gql`
  query VehicleRecallCounts($carId: ID) {
    vehicleRecallCounts(carId: $carId) {
      code
      data {
        active
        dismissed
        resolved
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const VEHICLE_RECALL_UPDATE_MUTATION = gql`
  mutation VehicleRecallUpdate($where: VehicleRecallWhereInput, $params: VehicleRecallUpdateInput) {
    vehicleRecallUpdate(where: $where, params: $params) {
      code
      data {
        ${VEHICLE_RECALL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_RECALL_REFRESH_MUTATION = gql`
  mutation VehicleRecallRefresh($where: VehicleRecallCarWhereInput) {
    vehicleRecallRefresh(where: $where) {
      code
      data {
        queued
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Vehicle recall user status constants
 */
export const VEHICLE_RECALL_STATUS = {
  /** Needs attention */
  ACTIVE: 100,
  /** User chose to ignore */
  DISMISSED: 5000,
  /** Repaired / fixed */
  RESOLVED: 10000,
} as const;

export type VehicleRecallStatusValue =
  (typeof VEHICLE_RECALL_STATUS)[keyof typeof VEHICLE_RECALL_STATUS];

/**
 * Recall data source constants
 */
export const VEHICLE_RECALL_SOURCE = {
  /** National Highway Traffic Safety Administration (US) */
  NHTSA: 'NHTSA',
  /** Transport Canada */
  TC: 'TC',
} as const;

export type VehicleRecallSourceValue =
  (typeof VEHICLE_RECALL_SOURCE)[keyof typeof VEHICLE_RECALL_SOURCE];

export interface VehicleRecall {
  /**
   * User-specific status row ID (from vehicle_recall_statuses).
   * NULL when the user has not yet acted on this recall.
   */
  id?: string | null | undefined;
  /** User's car this recall applies to */
  carId?: string | undefined;
  /** Reference to the shared recall catalog record */
  vehicleRecallId?: string | undefined;

  // User status fields
  /** When the user dismissed this recall */
  dismissedAt?: string | undefined;
  /** When the user marked this recall as resolved/repaired */
  resolvedAt?: string | undefined;
  /** User notes about this recall (e.g. repair details, dealer info) */
  userNotes?: string | undefined;
  /**
   * User status for this recall:
   * - 100: Active (needs attention)
   * - 5000: Dismissed (user chose to ignore)
   * - 10000: Resolved (repaired/fixed)
   */
  status?: VehicleRecallStatusValue | undefined;
  /** When this recall was first linked to the user's vehicle */
  createdAt?: string | undefined;

  // Recall detail fields (from shared catalog)
  /** Recall data source: NHTSA (US) or TC (Canada) */
  source?: VehicleRecallSourceValue | undefined;
  /** Unique campaign/recall number from the source */
  campaignNumber?: string | undefined;
  /** Manufacturer name as reported by the source */
  manufacturer?: string | undefined;
  /** Affected component (e.g. SUSPENSION:REAR) */
  component?: string | undefined;
  /** System type affected (primarily used by Transport Canada) */
  systemType?: string | undefined;
  /** Recall summary / description of the defect */
  summary?: string | undefined;
  /** Potential consequences of the defect */
  consequence?: string | undefined;
  /** Remedy / corrective action description */
  remedy?: string | undefined;
  /** Additional notes from the source */
  recallNotes?: string | undefined;
  /** Date the recall report was received by the authority (YYYY-MM-DD) */
  reportReceivedDate?: string | undefined;
  /** Vehicle should not be driven until repaired (NHTSA Do Not Drive) */
  parkIt?: boolean | undefined;
  /** Vehicle should be parked away from structures (fire/explosion risk) */
  parkOutside?: boolean | undefined;
  /** Recall can be resolved via over-the-air software update */
  otaUpdate?: boolean | undefined;

  // Resolved references
  /** Vehicle this recall applies to */
  car?: Car | undefined;
}

export interface VehicleRecallCounts {
  /** Number of active/open recalls needing attention */
  active?: number | undefined;
  /** Number of recalls dismissed by the user */
  dismissed?: number | undefined;
  /** Number of recalls marked as resolved/repaired */
  resolved?: number | undefined;
}

export interface VehicleRecallRefresh {
  /** Number of lookup records queued for re-fetch */
  queued?: number | undefined;
}

export interface VehicleRecallFilter {
  /** Filter by recall status ID(s) */
  id?: string[] | null | undefined;
  /** Filter by car ID(s) */
  carId?: string[] | null | undefined;
  /**
   * Filter by user status:
   * - 100: Active
   * - 5000: Dismissed
   * - 10000: Resolved
   */
  statusFilter?: VehicleRecallStatusValue[] | null | undefined;
  /** Search in summary, component, consequence, remedy, campaign number */
  searchKeyword?: string | null | undefined;
}

export interface VehicleRecallWhereInput {
  /** Vehicle recall status row ID (if user has already acted on this recall) */
  id?: string | null | undefined;
  /** Shared recall catalog record ID (required for first-time actions) */
  vehicleRecallId?: string | undefined;
  /** Car ID (required for first-time actions) */
  carId?: string | undefined;
}

export interface VehicleRecallUpdateInput {
  /**
   * New status for the recall:
   * - 100: Active (reopen a dismissed or resolved recall)
   * - 5000: Dismissed (user chose to ignore)
   * - 10000: Resolved (repaired/fixed)
   */
  status?: VehicleRecallStatusValue | null | undefined;
  /** User notes about this recall (repair details, dealer info, etc.) */
  notes?: string | null | undefined;
}

export interface VehicleRecallCarWhereInput {
  /** Car ID to refresh recalls for */
  carId?: string | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get human-readable label for recall status
 */
export function getRecallStatusLabel(status: VehicleRecallStatusValue): string {
  const labels: Record<VehicleRecallStatusValue, string> = {
    [VEHICLE_RECALL_STATUS.ACTIVE]: 'Active',
    [VEHICLE_RECALL_STATUS.DISMISSED]: 'Dismissed',
    [VEHICLE_RECALL_STATUS.RESOLVED]: 'Resolved',
  };
  return labels[status] || String(status);
}

/**
 * Get color for recall status (Mantine color names)
 */
export function getRecallStatusColor(status: VehicleRecallStatusValue): string {
  const colors: Record<VehicleRecallStatusValue, string> = {
    [VEHICLE_RECALL_STATUS.ACTIVE]: 'red',
    [VEHICLE_RECALL_STATUS.DISMISSED]: 'gray',
    [VEHICLE_RECALL_STATUS.RESOLVED]: 'green',
  };
  return colors[status] || 'gray';
}

/**
 * Get human-readable label for recall source
 */
export function getRecallSourceLabel(source: VehicleRecallSourceValue): string {
  const labels: Record<VehicleRecallSourceValue, string> = {
    [VEHICLE_RECALL_SOURCE.NHTSA]: 'NHTSA (US)',
    [VEHICLE_RECALL_SOURCE.TC]: 'Transport Canada',
  };
  return labels[source] || source;
}

/**
 * Check if a recall has safety-critical flags (parkIt or parkOutside)
 */
export function isRecallSafetyCritical(recall: VehicleRecall): boolean {
  return Boolean(recall.parkIt || recall.parkOutside);
}

/**
 * Get the effective identifier for a recall.
 * Uses the status row ID if available (user has acted),
 * otherwise falls back to vehicleRecallId (from shared catalog).
 */
export function getRecallEffectiveId(recall: VehicleRecall): string {
  return recall.id || recall.vehicleRecallId || '';
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Vehicle recall service that handles all recall-related API calls.
 * Supports listing recalls, updating user status, and refreshing recall data.
 */
export class VehicleRecallService {
  /**
   * Get a list of vehicle recalls with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: VehicleRecallFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_RECALL_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get recall counts by status, optionally filtered by car ID.
   * Useful for dashboard badges and summary displays.
   */
  static async counts(carId?: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_RECALL_COUNTS_QUERY, {
      carId: carId || undefined,
    });
    return result;
  }

  /**
   * Update a vehicle recall status (dismiss, resolve, reopen, or update notes).
   * Supports upsert: pass vehicleRecallId + carId for first-time actions
   * when no status row exists yet, or pass id for subsequent updates.
   */
  static async update(
    where: VehicleRecallWhereInput,
    params: VehicleRecallUpdateInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_RECALL_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Dismiss a recall (sets status to 5000).
   * Accepts either a status row ID or vehicleRecallId + carId for first-time actions.
   */
  static async dismiss(
    where: VehicleRecallWhereInput,
    notes?: string
  ): Promise<OpResult> {
    return VehicleRecallService.update(
      where,
      { status: VEHICLE_RECALL_STATUS.DISMISSED, notes: notes || undefined }
    );
  }

  /**
   * Resolve a recall (sets status to 10000).
   * Accepts either a status row ID or vehicleRecallId + carId for first-time actions.
   */
  static async resolve(
    where: VehicleRecallWhereInput,
    notes?: string
  ): Promise<OpResult> {
    return VehicleRecallService.update(
      where,
      { status: VEHICLE_RECALL_STATUS.RESOLVED, notes: notes || undefined }
    );
  }

  /**
   * Reopen a dismissed or resolved recall (sets status back to 100).
   */
  static async reopen(where: VehicleRecallWhereInput): Promise<OpResult> {
    return VehicleRecallService.update(
      where,
      { status: VEHICLE_RECALL_STATUS.ACTIVE }
    );
  }

  /**
   * Request a recall data refresh for a specific vehicle.
   * Queues the vehicle for background recall data re-fetch from NHTSA/Transport Canada.
   */
  static async refresh(carId: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_RECALL_REFRESH_MUTATION, {
      where: { carId },
    });
    return result;
  }
}